<script setup>
import { ref, onMounted, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import Draggable from 'vuedraggable';
import { useAlert } from 'dashboard/composables';
import PlaybooksAPI from 'dashboard/api/playbooks';
import Button from 'dashboard/components-next/button/Button.vue';
import ProjectEmptyState from './ProjectEmptyState.vue';

const props = defineProps({
  playbookId: {
    type: [String, Number],
    required: true,
  },
});

const { t } = useI18n();

const TYPE_KEYS = [
  'acta_constitutiva',
  'poder_notarial',
  'nda',
  'contrato',
  'propuesta',
  'factura',
  'identificacion',
  'otro',
];

const requests = ref([]);
const isLoading = ref(false);

const showForm = ref(false);
const isSaving = ref(false);
const emptyForm = () => ({ label: '', document_type: 'otro', required: true });
const form = ref(emptyForm());

const editingId = ref(null);
const editForm = ref(emptyForm());

const typeLabel = type =>
  TYPE_KEYS.includes(type) ? t(`PROJECTS.DOCUMENTS.TYPES.${type}`) : type;

const load = async () => {
  isLoading.value = true;
  try {
    const { data } = await PlaybooksAPI.getDocumentRequests(props.playbookId);
    requests.value = data || [];
  } catch (error) {
    requests.value = [];
  } finally {
    isLoading.value = false;
  }
};

const openForm = () => {
  form.value = emptyForm();
  showForm.value = true;
};
const cancelForm = () => {
  showForm.value = false;
};
const saveRequest = async () => {
  if (isSaving.value || !form.value.label.trim()) return;
  isSaving.value = true;
  try {
    await PlaybooksAPI.createDocumentRequest(props.playbookId, form.value);
    showForm.value = false;
    await load();
  } catch (error) {
    useAlert(t('PROJECTS.DOCUMENT_REQUESTS.SAVE_ERROR'));
  } finally {
    isSaving.value = false;
  }
};

const startEdit = request => {
  editingId.value = request.id;
  editForm.value = {
    label: request.label,
    document_type: TYPE_KEYS.includes(request.document_type)
      ? request.document_type
      : 'otro',
    required: request.required,
  };
};
const cancelEdit = () => {
  editingId.value = null;
};
const saveEdit = async id => {
  if (!editForm.value.label.trim()) return;
  try {
    await PlaybooksAPI.updateDocumentRequest(props.playbookId, id, editForm.value);
    editingId.value = null;
    await load();
  } catch (error) {
    useAlert(t('PROJECTS.DOCUMENT_REQUESTS.SAVE_ERROR'));
  }
};

const removeRequest = async id => {
  try {
    await PlaybooksAPI.deleteDocumentRequest(props.playbookId, id);
    requests.value = requests.value.filter(r => r.id !== id);
  } catch (error) {
    useAlert(t('PROJECTS.DOCUMENT_REQUESTS.DELETE_ERROR'));
  }
};

const onReorder = async () => {
  try {
    await PlaybooksAPI.reorderDocumentRequests(
      props.playbookId,
      requests.value.map(r => r.id)
    );
  } catch (error) {
    await load();
  }
};

watch(() => props.playbookId, load);
onMounted(load);
</script>

<template>
  <div class="flex flex-col gap-3">
    <Button
      v-if="!showForm"
      variant="faded"
      color="slate"
      size="sm"
      icon="i-lucide-plus"
      class="self-start"
      :label="t('PROJECTS.DOCUMENT_REQUESTS.ADD')"
      @click="openForm"
    />

    <div
      v-if="showForm"
      class="flex flex-col gap-2 p-3 border rounded-xl border-n-weak bg-n-solid-2"
    >
      <input
        v-model="form.label"
        type="text"
        :placeholder="t('PROJECTS.DOCUMENT_REQUESTS.LABEL_PLACEHOLDER')"
        class="w-full h-8 px-2 text-sm border rounded-md reset-base bg-n-alpha-black2 border-n-weak text-n-slate-12 placeholder:text-n-slate-10 focus:outline-none focus:outline-1 focus:outline-n-brand"
      />
      <div class="flex items-center gap-2">
        <label class="flex items-center gap-1.5 text-xs text-n-slate-10">
          {{ t('PROJECTS.DOCUMENT_REQUESTS.TYPE_LABEL') }}
          <select
            v-model="form.document_type"
            class="h-8 px-2 text-sm border rounded-md reset-base bg-n-alpha-black2 border-n-weak text-n-slate-12 focus:outline-none focus:outline-1 focus:outline-n-brand"
          >
            <option v-for="key in TYPE_KEYS" :key="key" :value="key">
              {{ t(`PROJECTS.DOCUMENTS.TYPES.${key}`) }}
            </option>
          </select>
        </label>
        <label class="flex items-center gap-1.5 text-xs text-n-slate-11">
          <input v-model="form.required" type="checkbox" />
          {{ t('PROJECTS.DOCUMENT_REQUESTS.REQUIRED') }}
        </label>
      </div>
      <div class="flex items-center justify-end gap-2">
        <Button
          variant="ghost"
          color="slate"
          size="sm"
          :label="t('PROJECTS.DOCUMENT_REQUESTS.CANCEL')"
          @click="cancelForm"
        />
        <Button
          size="sm"
          color="blue"
          :label="t('PROJECTS.DOCUMENT_REQUESTS.SAVE')"
          :is-loading="isSaving"
          @click="saveRequest"
        />
      </div>
    </div>

    <ProjectEmptyState
      v-if="!requests.length && !isLoading"
      compact
      icon="i-lucide-file-check-2"
      :title="t('PROJECTS.DOCUMENT_REQUESTS.EMPTY')"
    />

    <Draggable
      v-else
      v-model="requests"
      item-key="id"
      handle=".tpl-drag"
      class="flex flex-col divide-y divide-n-weak/60"
      @end="onReorder"
    >
      <template #item="{ element: request }">
        <div data-test="tpl-row" class="flex items-center gap-2 py-2.5">
          <span
            class="tpl-drag i-lucide-grip-vertical size-4 shrink-0 cursor-grab text-n-slate-9"
          />
          <template v-if="editingId === request.id">
            <input
              v-model="editForm.label"
              type="text"
              class="flex-1 h-8 px-2 text-sm border rounded-md reset-base bg-n-alpha-black2 border-n-weak text-n-slate-12 focus:outline-none focus:outline-1 focus:outline-n-brand"
            />
            <Button
              size="xs"
              color="blue"
              :label="t('PROJECTS.DOCUMENT_REQUESTS.SAVE')"
              @click="saveEdit(request.id)"
            />
            <Button
              variant="ghost"
              color="slate"
              size="xs"
              :label="t('PROJECTS.DOCUMENT_REQUESTS.CANCEL')"
              @click="cancelEdit"
            />
          </template>
          <template v-else>
            <div class="flex flex-col flex-1 min-w-0">
              <span class="text-sm truncate text-n-slate-12">{{ request.label }}</span>
              <span class="text-xs text-n-slate-10">
                {{
                  request.required
                    ? t('PROJECTS.DOCUMENT_REQUESTS.REQUIRED')
                    : t('PROJECTS.DOCUMENT_REQUESTS.OPTIONAL')
                }}
                <template v-if="request.document_type">· {{ typeLabel(request.document_type) }}</template>
              </span>
            </div>
            <Button
              v-tooltip.top="t('PROJECTS.DOCUMENT_REQUESTS.EDIT')"
              variant="ghost"
              color="slate"
              size="xs"
              icon="i-lucide-pencil"
              @click="startEdit(request)"
            />
            <Button
              v-tooltip.top="t('PROJECTS.DOCUMENT_REQUESTS.DELETE')"
              variant="ghost"
              color="ruby"
              size="xs"
              icon="i-lucide-x"
              :data-test="`del-${request.id}`"
              @click="removeRequest(request.id)"
            />
          </template>
        </div>
      </template>
    </Draggable>
  </div>
</template>
